import { createContext, useEffect, useState } from "react";
import { apiService } from "@/services/APIService";
import type { AxiosResponse } from "axios";
import { EndpointService } from "@/types/api/api";

const DEFAULT_MESSAGES = {
  busy_mode_message: "This venue is currently busy so orders may be delayed",
  off_premise_service_name: "Collection",
  email_off_premise_order_accepted_next_steps:
    "Your order will be prepared and ready for collection at your chosen time",
  email_off_premise_collection_steps: "Your order is ready to collect!",
};

export interface IMessaging {
  busy_mode_message: string;
  off_premise_service_name: string;
  email_off_premise_order_accepted_next_steps: string;
  email_off_premise_collection_steps: string;
}

interface IMessagingContext {
  messages: IMessaging;
  setMessages: (messages: IMessaging) => void;
  getMessagesForRestaurant: (restaurantId: string) => Promise<void>;
}

const MessagingContext = createContext<IMessagingContext>({
  messages: DEFAULT_MESSAGES,
  setMessages: (messages) => {},
  getMessagesForRestaurant: async (restaurantId) => {},
});

const MessagingProvider = ({ restaurantId, children }) => {
  const setMessages = (messages) => {
    setState((prevState) => {
      return {
        ...prevState,
        messages,
      };
    });
  };

  const getMessagesForRestaurant = async () => {
    try {
      const { data: messages } = await apiService.get<
        AxiosResponse<IMessaging>
      >(`/venues/${restaurantId}/customisation/messaging`, {
        endpointService: EndpointService.Gateway,
      });
      setMessages(messages);
    } catch (e) {
      console.error(e);
    }
  };

  const loadingState = {
    messages: DEFAULT_MESSAGES,
    setMessages,
    getMessagesForRestaurant,
  };

  const [state, setState] = useState(loadingState);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <No need for other dependencies>
  useEffect(() => {
    if (restaurantId) {
      getMessagesForRestaurant();
    }
  }, [restaurantId]);

  return (
    <MessagingContext.Provider value={state}>
      {children}
    </MessagingContext.Provider>
  );
};

export { MessagingProvider, MessagingContext };
